import React, { useContext, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Menu,
    MenuItem,
    Checkbox,
    TableSortLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Toolbar,
    Typography,
    Box,
    useMediaQuery,
    CircularProgress,
    Chip,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { MoreVert as MoreVertIcon, Delete as DeleteIcon, Download, Visibility, Settings } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import { AuthContext } from '../../utils/AuthContext';
import moment from 'moment';


export default function UsersTable({
    isLoading,
    users,
    page,
    rowsPerPage,
    totalRecords,
    onPageChange,
    onRowsPerPageChange,
    onDelete,
    setSelectedUser,
    setOpenUserFormDialog,
    setUserFormMode,
    setShowDeleteDialog,
    setOpenResetPasswordFormDialog,
    userType
}) {

    const { auth } = useContext(AuthContext);

    const columns = [
        { id: 'agent_id', label: 'Agent ID', minWidth: 100 },
        { id: 'full_name', label: 'Name', minWidth: 120 },
        { id: 'contact_number', label: 'Contact No.', minWidth: 120 },
        { id: 'secondary_number', label: '2nd Contact No.', minWidth: 140 },

        { id: 'email', label: 'Email', width: 120 },
        { id: 'status', label: 'Status', width: 100 },
        { id: 'date_of_birth', label: 'Birthday', width: 100 },
        {
            id: 'actions',
            label: 'Actions',
            width: 80,
            sortable: false,
        },
    ].filter(item => {
        if (userType === 'Admin' && (item.id === 'agent_id' || item.id === 'contact_number' || item.id === 'secondary_number')) {
            return false;
        }
        return true;
    });


    const isMobile = useMediaQuery('(max-width:600px)');
    const [visibleColumns, setVisibleColumns] = useState(columns.map((col) => col.id));
    const [openColumnDialog, setOpenColumnDialog] = useState(false);
    const [selected, setSelected] = useState([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [expandedCells, setExpandedCells] = useState({});

    const [selectedRows, setSelectedRows] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);




    const handleDeleteSelected = () => {
        console.log('Deleting rows:', selectedRows);
    };

    const handleMenuOpen = (event, rowId) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRowId(null);
    };

    const toggleColumnVisibility = (columnId) => {
        setVisibleColumns((prev) =>
            prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
        );
    };

    const handleOpenColumnDialog = () => {
        setOpenColumnDialog(true);
    };

    const handleCloseColumnDialog = () => {
        setOpenColumnDialog(false);
    };

    const isSelected = (id) => selected.includes(id);

    const isCellExpanded = (rowId, columnId) => {
        return expandedCells[`${rowId}-${columnId}`] || false;
    };

    const toggleShowFullText = (rowId, columnId) => {
        setExpandedCells((prev) => ({
            ...prev,
            [`${rowId}-${columnId}`]: !prev[`${rowId}-${columnId}`],
        }));
    };



    const mappedUsers = users.map((user, index) => ({
        id: user.id,
        agent_id: user.agent?.agent_id || 'N/A',
        full_name: user.full_name || 'N/A',
        contact_number: user.agent?.contact_number || 'N/A',
        secondary_number: user.agent?.secondary_number || 'N/A',
        email: user.email || 'N/A',
        status: user.status || 'N/A',
        date_of_birth: user.date_of_birth ? moment(user.date_of_birth, "YYYY-MM-DD").format("MM/DD/YYYY") : 'N/A',
        created_at: user.created_at
    }));

    const handleDeleteDialogOpen = () => {
        setOpenDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setOpenDeleteDialog(false);
    };

    const handleDelete = () => {
        onDelete(selected);
        setSelected([]);
        setOpenDeleteDialog(false);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedUsers = [...mappedUsers].sort((a, b) => {
        if (orderBy) {
            const isAsc = order === 'asc';
            if (typeof a[orderBy] === 'string') {
                return isAsc
                    ? a[orderBy].localeCompare(b[orderBy])
                    : b[orderBy].localeCompare(a[orderBy]);
            }
            return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
        }
        return 0;
    });

    const renderCellContent = (column, row) => {
        if (column.id === 'actions') {
            const isDeletable = (createdAt) => {
                const createdTime = new Date(createdAt);
                const currentTime = new Date();
                const diffInHours = (currentTime - createdTime) / (1000 * 60 * 60);
                return diffInHours < 1; // True if less than 1 hour
            };

            return (
                <>
                    <IconButton onClick={(event) => handleMenuOpen(event, row.id)}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={menuRowId === row.id}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={() => {
                            setSelectedUser(findUserById(row.id));
                            setOpenUserFormDialog(true);
                            setUserFormMode('update');
                            handleMenuClose();
                        }}>
                            <ModeEditOutlineOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                        </MenuItem>
                        <MenuItem onClick={() => {
                            setSelectedUser(findUserById(row.id));
                            setOpenResetPasswordFormDialog(true);
                            handleMenuClose();
                        }}>
                            <LockOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Reset Pass
                        </MenuItem>
                        {isDeletable(row.created_at) && (
                            <MenuItem sx={{ color: '#f44336' }} onClick={() => {
                                setSelectedUser(findUserById(row.id));
                                setShowDeleteDialog(true);
                                handleMenuClose();
                            }}>
                                <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                            </MenuItem>
                        )}
                    </Menu>
                </>
            );
        } else if (column.id === 'status') {
            const status = row[column.id];

            return <Chip
                label={status}
                sx={{
                    backgroundColor: getStatusBackgroundColor(status),
                    color: getStatusColor(status)
                }}
            />
        }
        else {
            return row[column.id];
        }
    };

    function findUserById(id) {
        for (let i = 0; i < users.length; i++) {
            if (users[i].id === id) {
                return users[i];
            }
        }
    }

    function getStatusBackgroundColor(status) {

        if (status === 'Active') {
            return '#EBF9F1';
        } else if (status === 'Inactive') {
            return '#FBE7E8';
        }

        return null;
    }

    function getStatusColor(status) {

        if (status === 'Active') {
            return '#1F9254';
        } else if (status === 'Inactive') {
            return '#A30D11';
        }

        return null;
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>

            {/* <Toolbar>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteDialogOpen}
                        sx={{
                            visibility: selected.length > 0 ? 'visible' : 'hidden', // Toggle visibility based on selected.length
                            mr: 2, // Space between button and table
                        }}
                    >
                        Delete {selected.length} {selected.length > 1 ? 'Items' : 'Item'}
                    </Button>
                    <Button
                        startIcon={<Settings />}
                        onClick={handleOpenColumnDialog}
                        variant="contained"
                    >
                        Column Settings
                    </Button>
                </Box>
            </Toolbar> */}

            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'end' }}>
                <Button
                    startIcon={<ViewWeekOutlinedIcon />}
                    onClick={handleOpenColumnDialog}
                    variant="text"
                >
                    Column Settings
                </Button>
            </Box>

            {/* Table */}
            <TableContainer variant='outlined' component={Paper} sx={{ flexGrow: 1, overflowY: 'auto', width: '99.50%' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {/* <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected.length > 0 && selected.length === mappedUsers.length}
                                        onChange={(event) =>
                                            setSelected(event.target.checked ? mappedUsers.map((row) => row.id) : [])
                                        }
                                    />
                                </TableCell> */}
                                {columns
                                    .filter((col) => visibleColumns.includes(col.id))
                                    .map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{ minWidth: column.minWidth, fontWeight: 600, color: '#424242' }}

                                        >
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(column.id)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedUsers.map((row) => (
                                <TableRow key={row.id}>
                                    {/* <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isSelected(row.id)}
                                            onChange={() =>
                                                setSelected((prev) =>
                                                    isSelected(row.id)
                                                        ? prev.filter((id) => id !== row.id)
                                                        : [...prev, row.id]
                                                )
                                            }
                                        />
                                    </TableCell> */}
                                    {columns
                                        .filter((col) => visibleColumns.includes(col.id))
                                        .map((column) => (
                                            <TableCell key={column.id} align={column.align}>
                                                {renderCellContent(column, row)}
                                            </TableCell>
                                        ))}
                                </TableRow>
                            ))}
                        </TableBody>

                    </Table>
                )}
            </TableContainer>
            <TablePagination
                sx={{ overflowY: 'hidden', minHeight: '45px', mb: 4 }}
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />

            {/* Column Visibility Dialog */}
            <Dialog open={openColumnDialog} onClose={handleCloseColumnDialog}>
                <DialogTitle>Column Settings</DialogTitle>
                <DialogContent>
                    {columns.map((column) => (
                        <FormControlLabel
                            key={column.id}
                            control={
                                <Checkbox
                                    checked={visibleColumns.includes(column.id)}
                                    onChange={() => toggleColumnVisibility(column.id)}
                                />
                            }
                            label={column.label}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseColumnDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
